import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Alert, Empty, ErrorState, Skeleton } from '@/components/States';
import { dateTime } from '@/lib/format';
import { KV, LegacyLink, Panel, ReadonlyNote } from './kit/Kit';
import { useBack } from './kit/hooks';
import { useAccountsAndRoles } from './usersData';
import { effectivePerms, scopeBrief } from './permissions';
import { SummaryBlocks } from './RoleDetail';
import { AccountSettingsSheet } from './AccountSettingsSheet';
import { activeState, baseRoleLabel } from './status';
import s from './AccountDetail.module.css';

/**
 * 账号详情（permissions-*.html A9 的只读版）：基本信息 + 「这个人现在能做什么」。
 * 改角色、停用、重置密码都去老版（手机上本来也不让替别人敲密码）。
 */
export function AccountDetail({ id }: { id: string }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const back = useBack('/users?tab=accounts');
  const data = useAccountsAndRoles();
  const u = data.users.find((x) => x.id === id);

  if (!data.canUsers) return <Empty icon="lock" title="没有查看账号的权限">需要「查看账号列表」权限。</Empty>;
  if (data.usersState.error) return <ErrorState title="账号加载失败" message={data.usersState.error.message} detail="GET /api/users" onRetry={data.usersState.reload} />;
  if (!u) {
    if (data.usersState.loading) return <div className={s.loading}><Skeleton /><Skeleton lines={5} /></div>;
    return <Empty icon="info" title="找不到这个账号">可能已被删除。</Empty>;
  }
  const role = data.roleOf(u);
  const st = activeState(u.isActive);
  const sc = role ? scopeBrief(role, effectivePerms(role)) : null;
  const phone = ff === 'phone';
  const writable = can('users.edit', 'users.status.write', 'users.password.reset');

  return (
    <div className={`${s.page} ${phone ? s.phone : ''}`}>
      <header className={s.head}>
        <button className={s.back} onClick={back} aria-label="返回"><Icon name="back" size={20} /></button>
        <span className={s.ava}>{u.name.slice(0, 1)}</span>
        <h1>{u.name}</h1>
        <Tag tone={st.tone} sm>{st.label}</Tag>
        <span className={s.sp} />
        {writable && <button type="button" className={s.settingsButton} onClick={() => setSettingsOpen(true)}>账号设置</button>}
        {writable && <LegacyLink path="/users">老版</LegacyLink>}
      </header>
      <div className={s.scroll}>
        <p className={s.sub}><span className="tnum">{u.username}</span> · {role?.name || u.roleDefinition?.name || u.roleCode || u.role} · 更新于 {dateTime(u.updatedAt)}</p>
        {!u.isActive && <Alert tone="warn" icon="lock" title="账号已停用">停用的账号不能登录。具有账号状态权限的管理员可在“账号设置”中启用。</Alert>}
        <div className={s.grid}>
          <Panel title="基本信息">
            <KV items={[
              ['登录账号', <span key="u" className="tnum">{u.username}</span>],
              ['姓名', u.name],
              ['角色', role ? <Link key="r" className={s.link} to={`/users/roles/${role.id}`}>{role.name}</Link> : (u.roleDefinition?.name || u.roleCode || u.role)],
              ['角色模板', baseRoleLabel[role?.baseRole || u.role] || u.role],
              ['数据范围', sc ? <Dot key="d" tone={sc.tone}>{sc.label}</Dot> : '—'],
              ['状态', u.isActive ? '启用' : '停用（不能登录）'],
              ['创建时间', <span key="c" className="tnum">{dateTime(u.createdAt)}</span>],
            ]} />
            <p className={s.hint}>账号的权限来自角色；改角色会立即切换整套权限。V2 本地可在“账号设置”中修改。</p>
          </Panel>
          {role ? (
            <Panel title={`${u.name}现在能做什么`} extra={<span className={s.from}>来自角色「{role.name}」</span>}>
              <SummaryBlocks role={role} />
            </Panel>
          ) : (
            <Panel title="权限">
              <p className="em">没有读到这个账号的角色定义（需要「查看角色列表」权限），无法生成摘要。</p>
            </Panel>
          )}
        </div>
        <ReadonlyNote />
      </div>
      {settingsOpen && <AccountSettingsSheet account={u} roles={data.roles} canEdit={can('users.edit')} canStatus={can('users.status.write')} canReset={can('users.password.reset')} onClose={() => setSettingsOpen(false)} onSaved={data.usersState.reload} />}
    </div>
  );
}

export function AccountDetailPage() {
  const { id } = useParams();
  return <AccountDetail id={id!} />;
}
